import dayjs, { Dayjs } from 'dayjs'

import { ConsultationTbScreening, TbWorkup } from 'src/api/requests'
import { yearMonthDaySlash } from 'src/libs'

const WORKUP_DATE_FIELDS = [
  'crpDate',
  'chestXrayDate',
  'lfLamDate',
  'xpertMtbRifDate',
  'phthisiatricianReferralDate'
] as const

type WorkupDateField = (typeof WORKUP_DATE_FIELDS)[number]

export type TbWorkupFormValues = Omit<TbWorkup, WorkupDateField> &
  Partial<Record<WorkupDateField, Dayjs | null>>

export type TbScreeningFormValues = Omit<ConsultationTbScreening, 'workup'> & {
  workup?: TbWorkupFormValues | null
}

export const isTbScreeningPositive = (values?: TbScreeningFormValues | null) =>
  !!values &&
  [
    values.cough,
    values.fever,
    values.weightLoss,
    values.nightSweats,
    values.recentTbContact
  ].some((answer) => answer === true)

/** Form values → request (dates as yyyy-mm-dd; work-up only when positive). */
export const tbScreeningToPayload = (
  values?: TbScreeningFormValues | null
): ConsultationTbScreening | undefined => {
  if (!values) return undefined

  const { workup, ...answers } = values
  const payload: ConsultationTbScreening = {
    cough: answers.cough,
    fever: answers.fever,
    weightLoss: answers.weightLoss,
    nightSweats: answers.nightSweats,
    recentTbContact: answers.recentTbContact,
    isHospitalized: answers.isHospitalized ?? false
  }

  if (!isTbScreeningPositive(values) || !workup) return payload

  const workupPayload: TbWorkup = {
    crpMgPerL: workup.crpMgPerL ?? null,
    chestXray: workup.chestXray ?? null,
    lfLam: workup.lfLam ?? null,
    xpertMtbRif: workup.xpertMtbRif ?? null,
    phthisiatricianReferral: !!workup.phthisiatricianReferral,
    phthisiatricianReferralNotes: workup.phthisiatricianReferralNotes ?? null
  }
  WORKUP_DATE_FIELDS.forEach((field) => {
    const date = workup[field]
    workupPayload[field] = date ? dayjs(date).format(yearMonthDaySlash) : null
  })

  return { ...payload, workup: workupPayload }
}

/** Response → form values (dates back to dayjs). */
export const tbScreeningToForm = (
  screening?: ConsultationTbScreening | null
): TbScreeningFormValues | undefined => {
  if (!screening) return undefined

  const { workup, ...answers } = screening
  if (!workup) return { ...answers, workup: undefined }

  const toDayjs = (date?: string | null) => (date ? dayjs(date) : null)
  const workupValues: TbWorkupFormValues = {
    ...workup,
    crpDate: toDayjs(workup.crpDate),
    chestXrayDate: toDayjs(workup.chestXrayDate),
    lfLamDate: toDayjs(workup.lfLamDate),
    xpertMtbRifDate: toDayjs(workup.xpertMtbRifDate),
    phthisiatricianReferralDate: toDayjs(workup.phthisiatricianReferralDate)
  }

  return { ...answers, workup: workupValues }
}
